/**
 * Voice assessment session.
 *
 * The client owns the question pointer, not the model. The backend hands over a
 * rules-only baseline prompt plus one instruction block per question, and this
 * hook pushes exactly one of those blocks at a time via session.update -- the
 * same approach useScriptedLessonSession takes with slides. The model therefore
 * cannot lose its place, re-ask, read a question that does not exist, or decide
 * on its own that the assessment is over.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import ApiClient from '../config/ApiClient';
import {
    useVoiceLiveSession,
    VoiceLiveFunctionArgs,
    VoiceLiveSessionConfig,
} from './useVoiceLiveSession';

/** Filter devtools console with this substring (e.g. text filter: voice-assessment). */
const VOICE_ASSESSMENT_DEBUG_PREFIX = '[voice-assessment]';

function voiceAssessmentDebug(message: string, ...data: unknown[]) {
    console.debug(VOICE_ASSESSMENT_DEBUG_PREFIX, message, ...data);
}

/** Grace period for the closing line before the mic and socket are torn down. */
const CLOSING_LINE_MS = 6000;
/** One silent retry; after that the athlete gets an explicit Resume button. */
const MAX_RECONNECT_ATTEMPTS = 1;

const MIN_RESPONSE_VALUE = 1;
const MAX_RESPONSE_VALUE = 5;

export type VoicePhase =
    | 'idle'
    | 'connecting'
    | 'playing_question'
    | 'listening'
    | 'processing'
    | 'playing_reprompt'
    | 'reconnecting'
    | 'completed'
    | 'error';

export interface VoiceQuestionEntry {
    question_number: number;
    assessment_question_id: number;
    instructions: string;
}

interface VoiceLiveTokenResponse {
    key: string;
    endpoint: string;
    model: string;
    baseline_prompt: string;
    questions: VoiceQuestionEntry[];
    start_question_number: number | null;
    answered_question_numbers: number[];
    total_questions: number;
    tools: VoiceLiveSessionConfig['tools'];
}

interface UseVoiceAssessmentOptions {
    assignmentId: number | null;
    submitResponse: (assessmentQuestionId: number, response: number) => Promise<boolean>;
    /** Absolute move, so the on-screen question can never drift from the voice pointer. */
    goToQuestion: (index: number) => void;
    submitAssessment: () => Promise<boolean>;
    onCompleted?: () => void;
}

const CLOSING_INSTRUCTIONS = `ASSESSMENT COMPLETE - there are no questions left and there is nothing more to read.
Say exactly: "Thank you, that completes your assessment."
Then stop. Do not ask anything else, do not summarize, and do not mention scores or what any answer means.`;

function toInteger(value: unknown): number | null {
    if (typeof value === 'number' && Number.isInteger(value)) return value;
    if (typeof value === 'string') {
        const parsed = Number.parseInt(value, 10);
        if (Number.isInteger(parsed)) return parsed;
    }
    return null;
}

export function useVoiceAssessment({
    assignmentId,
    submitResponse,
    goToQuestion,
    submitAssessment,
    onCompleted,
}: UseVoiceAssessmentOptions) {
    const [voiceActive, setVoiceActive] = useState(false);
    const [phase, setPhase] = useState<VoicePhase>('idle');
    const [transcript, setTranscript] = useState<string | null>(null);
    const [voiceError, setVoiceError] = useState<string | null>(null);
    const [canResume, setCanResume] = useState(false);

    const isActiveRef = useRef(false);
    const micLevelRef = useRef(0);

    // The client's authoritative view of progress.
    const questionNumberRef = useRef<number | null>(null);
    const answeredRef = useRef<Set<number>>(new Set());
    const questionsRef = useRef<VoiceQuestionEntry[]>([]);
    const baselinePromptRef = useRef('');
    const totalRef = useRef(0);

    const completedRef = useRef(false);
    const reconnectAttemptsRef = useRef(0);
    const closingTimerRef = useRef<number | null>(null);
    const startVoiceLiveRef = useRef<(isReconnect?: boolean) => Promise<void>>();

    /**
     * The context callbacks change identity on every AssessmentContext render.
     * Reading them through a ref keeps handleFunctionCall stable while still
     * calling the *current* versions: the callbacks registered with Voice Live
     * are captured once at connect time, and a stale submitAssessment closes over
     * an empty `responses` array, whose length check made voice completion a
     * silent no-op.
     */
    const contextRef = useRef({ submitResponse, goToQuestion, submitAssessment, onCompleted });
    contextRef.current = { submitResponse, goToQuestion, submitAssessment, onCompleted };

    const {
        connect,
        disconnect: disconnectSession,
        sendFunctionResult,
        sendSessionUpdate,
        cancelResponse,
        requestResponse,
        setCallbacks,
    } = useVoiceLiveSession();

    // --- Question pointer helpers ---

    const entryFor = useCallback((questionNumber: number): VoiceQuestionEntry | undefined => (
        questionsRef.current.find(entry => entry.question_number === questionNumber)
    ), []);

    const remainingCount = useCallback(
        () => Math.max(totalRef.current - answeredRef.current.size, 0),
        [],
    );

    /**
     * The next question to read: the first unanswered one after `afterNumber`,
     * falling back to the first unanswered anywhere. The fallback matters when
     * resuming a session with gaps, or after the athlete jumps backwards.
     */
    const nextUnansweredAfter = useCallback((afterNumber: number): number | null => {
        const ordered = questionsRef.current;
        const later = ordered.find(entry => (
            entry.question_number > afterNumber && !answeredRef.current.has(entry.question_number)
        ));
        if (later) return later.question_number;
        const anywhere = ordered.find(entry => !answeredRef.current.has(entry.question_number));
        return anywhere ? anywhere.question_number : null;
    }, []);

    /**
     * Make `questionNumber` current: move the on-screen question and swap the
     * model's instructions. `requestTurn` is false when the caller is about to
     * return a tool result, which triggers the turn by itself.
     */
    const pushQuestion = useCallback((questionNumber: number, requestTurn: boolean): boolean => {
        const entry = entryFor(questionNumber);
        if (!entry) {
            voiceAssessmentDebug('no instructions for question', questionNumber);
            return false;
        }
        questionNumberRef.current = questionNumber;
        contextRef.current.goToQuestion(questionNumber - 1);
        sendSessionUpdate(`${baselinePromptRef.current}\n\n${entry.instructions}`);
        if (requestTurn) requestResponse();
        return true;
    }, [entryFor, sendSessionUpdate, requestResponse]);

    // --- Teardown ---

    const cleanup = useCallback(() => {
        if (closingTimerRef.current != null) {
            window.clearTimeout(closingTimerRef.current);
            closingTimerRef.current = null;
        }
        disconnectSession();
        micLevelRef.current = 0;
    }, [disconnectSession]);

    /**
     * Finish the assessment: closing line, submit, then tear the session down.
     * Idempotent -- the model can emit complete_assessment more than once, and
     * the last recorded answer reaches here on its own.
     */
    const finishAssessment = useCallback(async (callId?: string) => {
        if (completedRef.current) {
            if (callId) sendFunctionResult(callId, { success: true, remaining: 0 });
            return;
        }
        completedRef.current = true;
        // Synchronous, not via an effect: onDisconnected checks this ref when the
        // socket closes below, and would otherwise raise a spurious error banner.
        isActiveRef.current = false;

        sendSessionUpdate(`${baselinePromptRef.current}\n\n${CLOSING_INSTRUCTIONS}`);
        if (callId) {
            sendFunctionResult(callId, { success: true, remaining: 0 }, { requestFollowUp: false });
        }
        requestResponse();

        setPhase('completed');
        setTranscript(null);

        const submitted = await contextRef.current.submitAssessment();
        if (!submitted) {
            voiceAssessmentDebug('submitAssessment returned false after voice completion');
            setVoiceError('Your answers were saved, but submitting the assessment failed.');
        }

        setVoiceActive(false);
        // Let the closing line play out before the mic and socket go away.
        closingTimerRef.current = window.setTimeout(() => {
            closingTimerRef.current = null;
            cleanup();
        }, CLOSING_LINE_MS);

        if (submitted) contextRef.current.onCompleted?.();
    }, [sendFunctionResult, sendSessionUpdate, requestResponse, cleanup]);

    // --- Function-call handler ---

    const handleFunctionCall = useCallback(async (
        name: string, callId: string, args: VoiceLiveFunctionArgs,
    ) => {
        voiceAssessmentDebug('functionCall', { name, args, pointer: questionNumberRef.current });

        if (completedRef.current) {
            sendFunctionResult(callId, {
                success: false,
                error: 'The assessment is already complete.',
            });
            return;
        }

        switch (name) {
            case 'record_response': {
                const questionNumber = questionNumberRef.current;
                const entry = questionNumber != null ? entryFor(questionNumber) : undefined;
                if (!entry) {
                    sendFunctionResult(callId, {
                        success: false,
                        error: 'No question is currently active.',
                    });
                    return;
                }

                const value = toInteger(args.parsed_value);
                if (value == null || value < MIN_RESPONSE_VALUE || value > MAX_RESPONSE_VALUE) {
                    // Stay on this question: an unusable value must not advance.
                    sendFunctionResult(callId, {
                        success: false,
                        error: 'The answer must be a whole number from 1 to 5. Ask again.',
                        current_question: questionNumber,
                    });
                    setPhase('listening');
                    return;
                }

                const utterance = typeof args.transcript === 'string' ? args.transcript : '';
                setPhase('processing');
                setTranscript(utterance || null);

                try {
                    await ApiClient.post(
                        '/api/assessments/voice-record-response',
                        {
                            assignment_id: assignmentId,
                            assessment_question_id: entry.assessment_question_id,
                            parsed_value: value,
                            transcript: utterance,
                        },
                        { 'Content-Type': 'application/json' },
                    );
                    await contextRef.current.submitResponse(entry.assessment_question_id, value);
                } catch (err: unknown) {
                    const message = err instanceof Error ? err.message : 'Failed to record the answer';
                    voiceAssessmentDebug('record_response failed', err);
                    sendFunctionResult(callId, {
                        success: false,
                        error: message,
                        current_question: questionNumber,
                    });
                    setPhase('listening');
                    return;
                }

                answeredRef.current.add(entry.question_number);

                if (questionNumberRef.current !== questionNumber) {
                    // Another tool call moved the pointer while this one was in
                    // flight. The answer is saved (the endpoint upserts), but
                    // advancing again here would skip a question.
                    voiceAssessmentDebug('stale record_response, not advancing', {
                        recorded: questionNumber,
                        pointer: questionNumberRef.current,
                    });
                    sendFunctionResult(callId, {
                        success: true,
                        recorded_question: questionNumber,
                        next_question: questionNumberRef.current,
                        remaining: remainingCount(),
                    }, { requestFollowUp: false });
                    return;
                }

                const next = nextUnansweredAfter(entry.question_number);

                if (next == null) {
                    await finishAssessment(callId);
                    return;
                }

                // Instructions first, tool result second: the result carries the
                // model's next turn, which must already see the new question.
                pushQuestion(next, false);
                sendFunctionResult(callId, {
                    success: true,
                    recorded_question: entry.question_number,
                    next_question: next,
                    remaining: remainingCount(),
                }, { requestFollowUp: false });
                requestResponse();
                setPhase('playing_question');
                return;
            }

            case 'revisit_question': {
                const requested = toInteger(args.question_number);
                if (requested == null || requested < 1 || requested > totalRef.current) {
                    sendFunctionResult(callId, {
                        success: false,
                        error: `This assessment has ${totalRef.current} questions, numbered 1 to ${totalRef.current}.`,
                        current_question: questionNumberRef.current,
                    });
                    return;
                }
                // The athlete interrupts to ask for this, and interrupt_response is
                // off, so the model is probably still mid-sentence on the old one.
                cancelResponse();
                pushQuestion(requested, false);
                sendFunctionResult(callId, {
                    success: true,
                    current_question: requested,
                    remaining: remainingCount(),
                }, { requestFollowUp: false });
                requestResponse();
                setPhase('playing_question');
                return;
            }

            case 'complete_assessment': {
                const remaining = remainingCount();
                if (remaining > 0) {
                    // The model decided it was finished while questions are still
                    // outstanding. Correct it and put it back on the right question
                    // rather than letting it end the assessment early.
                    const resumeAt = questionNumberRef.current != null
                        ? (answeredRef.current.has(questionNumberRef.current)
                            ? nextUnansweredAfter(questionNumberRef.current)
                            : questionNumberRef.current)
                        : nextUnansweredAfter(0);
                    voiceAssessmentDebug('premature complete_assessment rejected', { remaining, resumeAt });
                    if (resumeAt != null) pushQuestion(resumeAt, false);
                    sendFunctionResult(callId, {
                        success: false,
                        error: 'The assessment is not finished yet. Continue with the current question.',
                        current_question: resumeAt,
                        remaining,
                    }, { requestFollowUp: false });
                    requestResponse();
                    setPhase('playing_question');
                    return;
                }
                await finishAssessment(callId);
                return;
            }

            default:
                voiceAssessmentDebug('unhandled function call', name);
                sendFunctionResult(callId, { success: false, error: 'Unknown tool' });
                return;
        }
    }, [
        assignmentId,
        entryFor,
        nextUnansweredAfter,
        pushQuestion,
        remainingCount,
        finishAssessment,
        sendFunctionResult,
        requestResponse,
        cancelResponse,
    ]);

    // --- Voice Live session lifecycle ---

    const startVoiceLive = useCallback(async (isReconnect = false) => {
        if (!assignmentId) return;
        setPhase(isReconnect ? 'reconnecting' : 'connecting');
        setVoiceError(null);
        setCanResume(false);
        if (!isReconnect) {
            setTranscript(null);
            completedRef.current = false;
            reconnectAttemptsRef.current = 0;
        }

        try {
            const tokenRes = await ApiClient.get<VoiceLiveTokenResponse>(
                `/api/speech/token?assignment_id=${assignmentId}`,
            );
            if (!tokenRes.success || !tokenRes.data) {
                throw new Error(tokenRes.error || 'Failed to get Voice Live credentials');
            }
            const {
                key,
                endpoint,
                model,
                baseline_prompt,
                questions,
                start_question_number,
                answered_question_numbers,
                total_questions,
                tools,
            } = tokenRes.data;

            if (!questions?.length) {
                throw new Error('This assessment has no questions to read');
            }

            baselinePromptRef.current = baseline_prompt;
            questionsRef.current = questions;
            totalRef.current = total_questions || questions.length;
            // Union rather than replace: on a reconnect the server is authoritative
            // (every answer is persisted before we advance) but keeping local
            // knowledge guards against a lagging read.
            (answered_question_numbers || []).forEach(n => answeredRef.current.add(n));

            if (start_question_number == null) {
                // Everything was already answered before we connected, so there is
                // nothing to ask; finish rather than open a session.
                await finishAssessment();
                return;
            }

            const startNumber = answeredRef.current.has(start_question_number)
                ? (nextUnansweredAfter(start_question_number) ?? start_question_number)
                : start_question_number;
            const startEntry = entryFor(startNumber) || questions[0];

            setCallbacks({
                onSessionCreated: () => {
                    voiceAssessmentDebug('session created');
                    reconnectAttemptsRef.current = 0;
                    setPhase('listening');
                },
                onSpeechStarted: () => {
                    setPhase('listening');
                    micLevelRef.current = 0.5;
                },
                onSpeechStopped: () => {
                    micLevelRef.current = 0;
                },
                onTranscriptDone: (text: string) => {
                    voiceAssessmentDebug('user transcript', text);
                    setTranscript(text);
                },
                onFunctionCall: handleFunctionCall,
                onAudioOutputStarted: () => {
                    setPhase('playing_question');
                },
                onAudioOutputStopped: () => {
                    if (isActiveRef.current && !completedRef.current) setPhase('listening');
                },
                onError: (error: string) => {
                    voiceAssessmentDebug('session error', error);
                    setVoiceError(error);
                    setPhase('error');
                },
                onDisconnected: () => {
                    voiceAssessmentDebug('session disconnected');
                    if (!isActiveRef.current || completedRef.current) return;
                    if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
                        reconnectAttemptsRef.current += 1;
                        voiceAssessmentDebug('reconnecting', {
                            attempt: reconnectAttemptsRef.current,
                            question: questionNumberRef.current,
                        });
                        setPhase('reconnecting');
                        void startVoiceLiveRef.current?.(true);
                        return;
                    }
                    setVoiceError('The voice session was interrupted. Your answers so far are saved.');
                    setCanResume(true);
                    setPhase('error');
                },
            });

            const config: VoiceLiveSessionConfig = {
                key,
                endpoint,
                model,
                systemPrompt: `${baseline_prompt}\n\n${startEntry.instructions}`,
                tools,
            };

            await connect(config);
            questionNumberRef.current = startEntry.question_number;
            contextRef.current.goToQuestion(startEntry.question_number - 1);
            isActiveRef.current = true;
            voiceAssessmentDebug('connected to Voice Live', {
                question: startEntry.question_number,
                answered: answeredRef.current.size,
                total: totalRef.current,
            });
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to start voice session';
            voiceAssessmentDebug('Voice Live connect failed', err);
            setVoiceError(message);
            setCanResume(true);
            setPhase('error');
        }
    }, [
        assignmentId,
        connect,
        setCallbacks,
        handleFunctionCall,
        entryFor,
        nextUnansweredAfter,
        finishAssessment,
    ]);

    startVoiceLiveRef.current = startVoiceLive;

    useEffect(() => () => { cleanup(); }, [cleanup]);

    // --- Start / stop triggers ---

    useEffect(() => {
        if (!voiceActive) return;
        voiceAssessmentDebug('starting Voice Live');
        void startVoiceLive();
        // startVoiceLive is intentionally omitted: it changes identity with the
        // context callbacks, and re-running would open a second session.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [voiceActive]);

    const startVoiceMode = useCallback(() => {
        voiceAssessmentDebug('startVoiceMode');
        answeredRef.current = new Set();
        questionNumberRef.current = null;
        completedRef.current = false;
        reconnectAttemptsRef.current = 0;
        setVoiceActive(true);
        setVoiceError(null);
        setCanResume(false);
        setTranscript(null);
        setPhase('idle');
    }, []);

    const stopVoiceMode = useCallback(() => {
        voiceAssessmentDebug('stopVoiceMode');
        // Before cleanup, so the resulting socket close is not read as a drop.
        isActiveRef.current = false;
        setVoiceActive(false);
        setPhase('idle');
        setTranscript(null);
        setCanResume(false);
        cleanup();
    }, [cleanup]);

    /** Re-establish a session that dropped, picking up at the current question. */
    const resumeVoiceMode = useCallback(() => {
        voiceAssessmentDebug('resumeVoiceMode');
        reconnectAttemptsRef.current = 0;
        setCanResume(false);
        setVoiceError(null);
        if (!voiceActive) {
            setVoiceActive(true);
            return;
        }
        void startVoiceLive(true);
    }, [voiceActive, startVoiceLive]);

    return {
        voiceActive,
        phase,
        transcript,
        voiceError,
        canResume,
        micLevelRef,
        startVoiceMode,
        stopVoiceMode,
        resumeVoiceMode,
    };
}
